import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Paperclip, Send, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, ROLE_LABEL } from "@/lib/constants";
import { downloadFile } from "@/lib/training";
import { PageHeader, EmptyState } from "@/components/Badges";
import { FeedbackStatus, Replies, FEEDBACK_STATUS } from "@/pages/Feedback";

const LINK_PATH = { sop: "/sops/", course: "/courses/", form: "/forms/" };

function Detail({ fb, onChanged }) {
  const [note, setNote] = useState("");
  const call = async (fn, msg) => {
    try {
      onChanged((await fn()).data);
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="rounded-2xl border border-lasso-line bg-white p-6" data-testid="feedback-detail">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-display text-lg font-bold">{fb.subject}</p>
          <p className="mt-1 text-xs text-zinc-500">{fb.user_name} · {ROLE_LABEL[fb.user_role]} · {fb.scope_label} · {formatDate(fb.created_at)}</p>
        </div>
        <Select value={fb.status} onValueChange={(v) => call(() => api.post(`/admin/feedback/${fb.id}/status`, { status: v }), "Status updated")}>
          <SelectTrigger className="w-36" data-testid="feedback-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(FEEDBACK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-400">{fb.category}{fb.priority === "urgent" ? " · URGENT" : ""}</p>
      <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-zinc-700">{fb.message}</p>
      <div className="mt-4 flex flex-wrap gap-4 text-sm">
        {fb.link_title && <Link to={`${LINK_PATH[fb.link_type]}${fb.link_id}`} className="font-semibold text-lasso-deep hover:text-lasso-ink" data-testid="feedback-detail-link">Related {fb.link_type}: {fb.link_title}</Link>}
        {fb.attachment_name && <button onClick={() => downloadFile(`/feedback/${fb.id}/attachment`, fb.attachment_name)} className="inline-flex items-center gap-1 font-semibold text-lasso-deep" data-testid="feedback-detail-attachment"><Paperclip className="h-4 w-4" /> {fb.attachment_name}</button>}
        <span className="inline-flex items-center gap-1 text-xs text-zinc-500" data-testid="feedback-detail-email-status"><Mail className="h-3.5 w-3.5" /> Email to {fb.email_to}: {fb.email_status}</span>
      </div>
      <Replies replies={fb.replies} />
      <div className="mt-5 space-y-2">
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write a reply note — it's emailed to the manager and shown in their Feedback page" data-testid="feedback-reply-input" />
        <button disabled={!note.trim()} onClick={() => call(() => api.post(`/admin/feedback/${fb.id}/reply`, { note }), "Reply sent").then(() => setNote(""))} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-50" data-testid="feedback-reply-button">
          <Send className="h-4 w-4" /> Send reply
        </button>
      </div>
    </div>
  );
}

function RecipientSetting() {
  const [to, setTo] = useState("");
  useEffect(() => { api.get("/admin/feedback-settings").then((r) => setTo(r.data.feedback_to)); }, []);
  const save = () => api.put("/admin/feedback-settings", { feedback_to: to }).then(() => toast.success("Feedback recipient saved")).catch((e) => toast.error(formatApiError(e)));
  return (
    <div className="mb-8 flex flex-col gap-2 rounded-2xl border border-lasso-line bg-white p-4 sm:flex-row sm:items-center">
      <p className="text-sm font-semibold sm:w-56">Feedback emails go to</p>
      <Input value={to} onChange={(e) => setTo(e.target.value)} className="sm:max-w-xs" data-testid="feedback-recipient-input" />
      <button onClick={save} className="rounded-full border border-lasso-line px-4 py-2 text-sm font-semibold hover:border-lasso-ink" data-testid="feedback-recipient-save">Save</button>
    </div>
  );
}

export default function FeedbackInbox() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(params.get("id"));
  const load = useCallback(() => api.get("/admin/feedback", { params: status === "all" ? {} : { status } }).then((r) => setData(r.data)), [status]);
  useEffect(() => { load(); }, [load]);

  const current = data?.items.find((x) => x.id === selected);
  const changed = (fb) => setData((d) => ({ ...d, items: d.items.map((x) => (x.id === fb.id ? fb : x)) }));
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Admin" title="Feedback inbox" description="Everything managers have sent from Lasso Hub." />
      {user?.is_super && <RecipientSetting />}
      <div className="mb-4 flex flex-wrap gap-2">
        {["all", "new", "in_review", "resolved"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${status === s ? "bg-lasso-ink text-white" : "border border-lasso-line bg-white"}`} data-testid={`feedback-filter-${s}`}>
            {s === "all" ? "All" : FEEDBACK_STATUS[s].label}{s !== "all" && data ? ` (${data.counts[s]})` : ""}
          </button>
        ))}
      </div>
      {data?.items.length === 0 ? <EmptyState title="No feedback yet" testId="feedback-inbox-empty" /> : (
        <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
          <div className="space-y-2" data-testid="feedback-inbox-list">
            {data?.items.map((fb) => (
              <button key={fb.id} onClick={() => setSelected(fb.id)} className={`w-full rounded-xl border p-4 text-left transition-colors ${selected === fb.id ? "border-lasso-ink bg-white" : "border-lasso-line bg-white hover:border-zinc-400"}`} data-testid={`feedback-inbox-item-${fb.id}`}>
                <div className="flex items-start justify-between gap-2"><p className="text-sm font-semibold">{fb.subject}</p><FeedbackStatus status={fb.status} /></div>
                <p className="mt-1 text-xs text-zinc-500">{fb.user_name} · {fb.category}{fb.priority === "urgent" ? " · Urgent" : ""} · {formatDate(fb.created_at)}</p>
              </button>
            ))}
          </div>
          {current ? <Detail key={current.id} fb={current} onChanged={changed} /> : <p className="text-sm text-muted-foreground">Select a submission.</p>}
        </div>
      )}
    </div>
  );
}
